/**
 * Per-conversation chat lock — local PIN gate on an already-unlocked device
 * (hides a chat from someone holding your phone; it is not E2EE).
 *
 * v2 derives the check value with PBKDF2-SHA-256 (310k iterations, random
 * 16-byte salt — OWASP 2023) so a copied record can't be brute-forced
 * instantly; v1 records (single salted SHA-256) still verify.
 */

export interface ChatLockRecord {
  conversationId: string;
  /** v1: SHA-256 hex of pin + conversationId; v2: PBKDF2 output hex. */
  pinHash: string;
  enabledAt: number;
  v?: 2;
  /** v2 random salt (hex). */
  salt?: string;
  iterations?: number;
}

export const CHAT_LOCK_PBKDF2_ITERATIONS = 310_000;

function toHex(bytes: Uint8Array): string {
  return [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
}

function fromHex(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

/** Constant-time comparison of equal-length hex strings. */
function ctEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

/** @deprecated v1 derivation — kept to verify locks created before v2. */
export async function hashChatPin(conversationId: string, pin: string): Promise<string> {
  const data = new TextEncoder().encode(`veto/chat-lock/v1|${conversationId}|${pin}`);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return toHex(new Uint8Array(digest));
}

async function pbkdf2Hex(conversationId: string, pin: string, salt: Uint8Array, iterations: number): Promise<string> {
  const key = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(`veto/chat-lock/v2|${conversationId}|${pin}`),
    "PBKDF2",
    false,
    ["deriveBits"],
  );
  const bits = await crypto.subtle.deriveBits(
    { name: "PBKDF2", hash: "SHA-256", salt: salt as BufferSource, iterations },
    key,
    256,
  );
  return toHex(new Uint8Array(bits));
}

export async function verifyChatPin(lock: ChatLockRecord, pin: string): Promise<boolean> {
  if (lock.v === 2 && lock.salt && lock.iterations) {
    const iterations = Math.min(Math.max(lock.iterations, 100_000), 5_000_000);
    return ctEqual(await pbkdf2Hex(lock.conversationId, pin, fromHex(lock.salt), iterations), lock.pinHash);
  }
  return ctEqual(await hashChatPin(lock.conversationId, pin), lock.pinHash);
}

export async function createChatLock(conversationId: string, pin: string): Promise<ChatLockRecord> {
  if (pin.length < 4) throw new Error("PIN must be at least 4 characters");
  const salt = crypto.getRandomValues(new Uint8Array(16));
  return {
    conversationId,
    v: 2,
    salt: toHex(salt),
    iterations: CHAT_LOCK_PBKDF2_ITERATIONS,
    pinHash: await pbkdf2Hex(conversationId, pin, salt, CHAT_LOCK_PBKDF2_ITERATIONS),
    enabledAt: Date.now(),
  };
}
